/**
 * Stream F — ads connection contract normalization.
 *
 * Manual account connection aligned to the real mounted /v1/ads account API.
 * Real apiFetch only. No mocks.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import {
  Link2,
  Unlink,
  ArrowRight,
  CheckCircle2,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import {
  buildAdsAccountConnectPayload,
  normalizeAdsAccount,
  type AdsAccountApiRecord,
  type AdsConnectionFormInput,
  type NormalizedAdsAccount,
} from "./connect.utils";

const PLATFORMS = [
  {
    id: "meta" as const,
    name: "Meta (Facebook / Instagram)",
    description: "Import campaigns, audiences, and conversion data",
  },
  {
    id: "google" as const,
    name: "Google Ads",
    description: "Import search, display, and YouTube campaigns",
  },
  {
    id: "tiktok" as const,
    name: "TikTok Ads",
    description: "Import TikTok ad campaigns and metrics",
  },
] as const;

type PlatformId = (typeof PLATFORMS)[number]["id"];
type Step = "select" | "auth" | "confirm";

const EMPTY_FORM: Omit<AdsConnectionFormInput, "platform"> = {
  accountName: "",
  externalAccountId: "",
  accessToken: "",
  refreshToken: "",
  currency: "USD",
  timezone: "",
};

const OAUTH_PLATFORMS: readonly PlatformId[] = ["meta", "google"];

export default function PlatformConnectionPage() {
  const [step, setStep] = useState<Step>("select");
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformId | null>(null);
  const [accounts, setAccounts] = useState<NormalizedAdsAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isStartingOauth, setIsStartingOauth] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: AdsAccountApiRecord[] }>("/v1/ads/accounts");
      setAccounts((res.data ?? []).map(normalizeAdsAccount));
    } catch {
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const oauthError = params.get("error");

    if (connected === "meta" || connected === "google") {
      setStep("confirm");
      setSelectedPlatform(connected as PlatformId);
      void fetchAccounts();
    }

    if (oauthError) {
      if (oauthError.startsWith("google")) {
        setError(`Google Ads connection failed: ${oauthError.replace(/_/g, " ")}`);
      } else {
        setError(`Meta connection failed: ${oauthError.replace(/_/g, " ")}`);
      }
    }
  }, [fetchAccounts]);

  const updateForm = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleConnect = async () => {
    if (!selectedPlatform) {
      return;
    }

    const payload = buildAdsAccountConnectPayload({
      platform: selectedPlatform,
      ...form,
    });

    if (!payload.accountName || !payload.externalAccountId || !payload.accessToken) {
      setError("Account name, external account ID, and access token are required.");
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      await apiFetch("/v1/ads/accounts/connect", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      await fetchAccounts();
      setForm(EMPTY_FORM);
      setStep("confirm");
    } catch {
      setError("Failed to connect the ad account. Check the account details and token, then try again.");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleMetaOauth = async () => {
    setIsStartingOauth(true);
    setError(null);

    try {
      const response = await apiFetch<{ authUrl: string }>("/v1/ads/meta/connect");
      window.location.href = response.authUrl;
    } catch {
      setError("Failed to start the Meta OAuth flow. Check the integration configuration and try again.");
      setIsStartingOauth(false);
    }
  };

  const handleGoogleOauth = async () => {
    setIsStartingOauth(true);
    setError(null);

    try {
      const response = await apiFetch<{ authUrl: string }>("/v1/ads/google/connect");
      window.location.href = response.authUrl;
    } catch {
      setError("Failed to start the Google Ads OAuth flow. Check the integration configuration and try again.");
      setIsStartingOauth(false);
    }
  };

  const handleDisconnect = async (accountId: string, accountName: string) => {
    const confirmed = window.confirm(`Disconnect ${accountName}? Historical data will be preserved.`);
    if (!confirmed) {
      return;
    }

    try {
      await apiFetch(`/v1/ads/accounts/${accountId}`, { method: "DELETE" });
      await fetchAccounts();
    } catch {
      setError("Failed to disconnect account.");
    }
  };

  const platformInfo = PLATFORMS.find((platform) => platform.id === selectedPlatform);
  const isOauthPlatform = selectedPlatform !== null && OAUTH_PLATFORMS.includes(selectedPlatform);

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <Link2 className="h-5 w-5 text-[var(--color-primary)]" />
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Connect Ad Platforms</h1>
      </div>

      {error ? (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      <section>
        <h2 className="text-sm font-medium text-[var(--color-muted-foreground)] uppercase tracking-wider mb-3">Connected Accounts</h2>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((item) => (
              <SkeletonCard key={item} className="h-16 rounded-xl" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center text-sm text-[var(--color-muted-foreground)]">
            No platforms connected yet. Select one below to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="stagger-item flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-primary)]/10">
                    <Link2 className="h-4 w-4 text-[var(--color-primary)]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--color-foreground)]">{account.accountName}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">
                      {account.platform.charAt(0).toUpperCase() + account.platform.slice(1)} · {account.accountId}
                    </p>
                    <p className="text-[10px] text-[var(--color-muted-foreground)]">
                      Connected {formatDate(account.connectedAt)} · {account.currency}
                    </p>
                    {account.lastSyncError ? (
                      <p className="text-[10px] text-[var(--color-error)]">{account.lastSyncError}</p>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      account.status === "active"
                        ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                        : account.status === "error"
                          ? "bg-[var(--color-error)]/10 text-[var(--color-error)]"
                          : "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                    }`}
                  >
                    {account.status}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDisconnect(account.id, account.accountName)}
                    className="text-[var(--color-muted-foreground)] hover:text-[var(--color-error)]"
                    title="Disconnect"
                  >
                    <Unlink className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {step === "select" ? (
        <section>
          <h2 className="text-sm font-medium text-[var(--color-muted-foreground)] uppercase tracking-wider mb-3">Add Platform</h2>
          <div className="space-y-2">
            {PLATFORMS.map((platform) => {
              const isConnected = accounts.some(
                (account) => account.platform === platform.id && account.status === "active",
              );

              return (
                <button
                  type="button"
                  key={platform.id}
                  disabled={isConnected}
                  onClick={() => {
                    setSelectedPlatform(platform.id);
                    setStep("auth");
                    setError(null);
                  }}
                  className="stagger-item w-full flex items-center gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 hover:bg-[var(--color-surface)] transition-colors text-left disabled:opacity-50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-surface)]">
                    <Link2 className="h-5 w-5 text-[var(--color-primary)]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[var(--color-foreground)]">{platform.name}</p>
                    <p className="text-sm text-[var(--color-muted-foreground)]">{platform.description}</p>
                  </div>
                  {isConnected ? (
                    <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />
                  ) : (
                    <ArrowRight className="h-5 w-5 text-[var(--color-muted-foreground)]" />
                  )}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {step === "auth" && platformInfo ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="mb-6 flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-primary)]/10">
              <KeyRound className="h-6 w-6 text-[var(--color-primary)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">
                Connect {platformInfo.name}
              </h2>
              <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
                {selectedPlatform === "meta"
                  ? "Meta now uses the real redirect-based OAuth flow. Tokens stay on the backend and connected ad accounts are discovered automatically."
                  : selectedPlatform === "google"
                    ? "Google Ads now uses the real OAuth flow with the restricted adwords scope. Refresh and access tokens stay on the backend and accessible customer accounts are discovered automatically."
                  : "This page is wired to the live mounted ads account API. Enter the platform account details directly."}
              </p>
            </div>
          </div>

          {selectedPlatform === "meta" || selectedPlatform === "google" ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-[var(--color-primary)]/15 bg-[var(--color-primary)]/5 px-4 py-3 text-sm">
                <div className="flex items-center gap-2 font-medium text-[var(--color-primary)]">
                  <ShieldCheck className="h-4 w-4" />
                  Real OAuth handoff
                </div>
                <p className="mt-1 text-[var(--color-muted-foreground)]">
                  {selectedPlatform === "meta"
                    ? "Stero will request Meta Ads permissions, exchange the code server-side, encrypt the long-lived access token, and auto-link every ad account visible to the authenticated user."
                    : "Stero will request Google Ads access, exchange the code server-side, store the refresh-capable token set encrypted at rest, and auto-link every accessible Google Ads customer visible to the authenticated user."}
                </p>
              </div>

              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
                Connected accounts will appear here after the callback completes. You can then run a manual sync or leave the 6-hour schedule in place.
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-[var(--color-primary)]/15 bg-[var(--color-primary)]/5 px-4 py-3 text-sm mb-6">
                <div className="flex items-center gap-2 font-medium text-[var(--color-primary)]">
                  <ShieldCheck className="h-4 w-4" />
                  Live contract requirements
                </div>
                <p className="mt-1 text-[var(--color-muted-foreground)]">
                  `accountName`, `externalAccountId`, and `accessToken` are required by the mounted backend. `refreshToken`, `currency`, and `timezone` are optional.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Account Name</label>
                  <input
                    value={form.accountName}
                    onChange={(e) => updateForm("accountName", e.target.value)}
                    placeholder={`${platformInfo.name} main account`}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">External Account ID</label>
                  <input
                    value={form.externalAccountId}
                    onChange={(e) => updateForm("externalAccountId", e.target.value)}
                    placeholder="e.g. 123456789"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Currency</label>
                  <input
                    value={form.currency}
                    onChange={(e) => updateForm("currency", e.target.value)}
                    placeholder="USD"
                    maxLength={3}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Access Token</label>
                  <textarea
                    value={form.accessToken}
                    onChange={(e) => updateForm("accessToken", e.target.value)}
                    rows={3}
                    placeholder="Paste the platform access token"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Refresh Token</label>
                  <input
                    value={form.refreshToken}
                    onChange={(e) => updateForm("refreshToken", e.target.value)}
                    placeholder="Optional"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Timezone</label>
                  <input
                    value={form.timezone}
                    onChange={(e) => updateForm("timezone", e.target.value)}
                    placeholder="Optional"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setStep("select");
                setSelectedPlatform(null);
                setError(null);
              }}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={selectedPlatform === "meta" ? handleMetaOauth : selectedPlatform === "google" ? handleGoogleOauth : handleConnect}
              disabled={isOauthPlatform ? isStartingOauth : isConnecting}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {(isOauthPlatform ? isStartingOauth : isConnecting) ? (
                <>
                  <Skeleton className="h-4 w-4 bg-white/50" />
                  <Skeleton className="h-4 w-20 bg-white/50" />
                </>
              ) : (
                <>
                  {selectedPlatform === "meta"
                    ? "Continue with Meta"
                    : selectedPlatform === "google"
                      ? "Continue with Google"
                      : "Save Connection"}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      ) : null}

      {step === "confirm" ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[var(--color-success)] mb-4" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-2">Connected Successfully</h2>
          <p className="text-sm text-[var(--color-muted-foreground)] mb-6">
            {selectedPlatform === "meta"
              ? "Meta OAuth completed, the token is now stored server-side, and discovered ad accounts are ready for campaign sync."
              : selectedPlatform === "google"
                ? "Google Ads OAuth completed, the refresh-capable token set is stored server-side, and discovered customer accounts are ready for campaign sync."
              : "The account was saved through the live ads account API and is now available for dashboard and campaign reads."}
          </p>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setStep("select");
                setSelectedPlatform(null);
              }}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
            >
              Connect Another
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("select");
                setSelectedPlatform(null);
                fetchAccounts();
              }}
              className="rounded-lg bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
